import React, { useEffect, useState } from "react";
import { ListBullets, CaretDown } from "@phosphor-icons/react";
import PageHero from "./PageHero";
import { COMPANY } from "../../data/site";

// Tracks which section heading is currently in view so the TOC can highlight it.
function useActiveSection(toc) {
  const [activeId, setActiveId] = useState(toc?.[0]?.id);

  useEffect(() => {
    if (!toc?.length || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-96px 0px -70% 0px" }
    );

    toc.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [toc]);

  return activeId;
}

function TocLinks({ toc, activeId, onNavigate }) {
  return (
    <ol className="space-y-0.5">
      {toc.map(({ id, title }, index) => {
        const active = id === activeId;
        return (
          <li key={id}>
            <a
              href={`#${id}`}
              onClick={onNavigate}
              className={`group flex gap-2.5 rounded-lg px-3 py-1.5 text-[13px] leading-snug transition-colors ${
                active
                  ? "bg-surfaceBlue text-brandNavy font-semibold"
                  : "text-ink500 hover:text-ink900 hover:bg-surface"
              }`}
            >
              <span
                className={`w-5 flex-shrink-0 tabular-nums text-right ${
                  active ? "text-brandOrange" : "text-ink500/70"
                }`}
              >
                {index + 1}.
              </span>
              <span>{title}</span>
            </a>
          </li>
        );
      })}
    </ol>
  );
}

export default function LegalLayout({
  title,
  eyebrow,
  description,
  lastUpdated,
  toc,
  children,
  testId,
}) {
  const activeId = useActiveSection(toc);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const hasToc = Boolean(toc?.length);

  const card = (
    <div className="bg-surfaceAlt border border-line rounded-3xl p-8 sm:p-14 min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-3 pb-6 mb-6 border-b border-line">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-ink500 mb-1">
            {COMPANY.legalName}
          </div>
          <div className="text-xs text-ink500">CIN: {COMPANY.cin}</div>
        </div>
        <div className="text-xs text-ink500">
          Last updated:{" "}
          <span className="font-semibold text-ink900">
            {lastUpdated || "December 2025"}
          </span>
        </div>
      </div>

      <p className="text-sm text-ink700 leading-relaxed mb-6">
        {COMPANY.attribution}.
      </p>

      {hasToc && (
        <div className="lg:hidden mb-8 rounded-2xl border border-line bg-surface">
          <button
            type="button"
            onClick={() => setMobileTocOpen((open) => !open)}
            aria-expanded={mobileTocOpen}
            data-testid={`${testId}-toc-toggle`}
            className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-sm font-bold text-ink900"
          >
            <span className="inline-flex items-center gap-2">
              <ListBullets size={18} weight="bold" className="text-brandBlue" />
              Contents · {toc.length} sections
            </span>
            <CaretDown
              size={16}
              weight="bold"
              className={`transition-transform ${mobileTocOpen ? "rotate-180" : ""}`}
            />
          </button>
          {mobileTocOpen && (
            <div className="px-2 pb-3 border-t border-line pt-2">
              <TocLinks
                toc={toc}
                activeId={activeId}
                onNavigate={() => setMobileTocOpen(false)}
              />
            </div>
          )}
        </div>
      )}

      <article className="legal-prose text-ink700 leading-relaxed text-[15px] space-y-5">
        {children}
      </article>
    </div>
  );

  return (
    <div data-testid={testId}>
      <PageHero
        eyebrow={eyebrow || "Legal"}
        title={title}
        description={description}
        testId={`${testId}-hero`}
      />
      <section className="py-12 sm:py-20">
        {hasToc ? (
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-10 grid lg:grid-cols-[270px_minmax(0,1fr)] gap-8 items-start">
            <aside className="hidden lg:block sticky top-24" data-testid={`${testId}-toc`}>
              <div className="bg-surfaceAlt border border-line rounded-3xl p-4 max-h-[calc(100vh-8rem)] overflow-y-auto">
                <div className="px-3 pt-1 pb-3 text-[10px] uppercase tracking-[0.22em] font-bold text-brandOrange">
                  On this page
                </div>
                <TocLinks toc={toc} activeId={activeId} />
              </div>
            </aside>
            {card}
          </div>
        ) : (
          <div className="max-w-5xl mx-auto px-5 sm:px-8">{card}</div>
        )}
      </section>
      <style>{`
        .legal-prose h2 {
          font-family: 'Cabinet Grotesk', 'IBM Plex Sans', sans-serif;
          color: var(--color-foreground);
          font-weight: 800;
          font-size: 1.35rem;
          letter-spacing: -0.01em;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          scroll-margin-top: 6rem;
        }
        .legal-prose h3 {
          color: var(--color-foreground);
          font-weight: 700;
          font-size: 1.05rem;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
        }
        .legal-prose p { margin-bottom: 0.85rem; }
        .legal-prose ul { list-style: disc; padding-left: 1.25rem; margin-bottom: 0.85rem; }
        .legal-prose ol { list-style: decimal; padding-left: 1.25rem; margin-bottom: 0.85rem; }
        .legal-prose li { margin-bottom: 0.35rem; }
        .legal-prose strong { color: var(--color-foreground); font-weight: 700; }
        .legal-prose a { color: var(--color-secondary); font-weight: 600; }
        .legal-prose a:hover { color: var(--color-foreground); }
        .legal-prose .legal-plain { list-style: none; padding-left: 0; margin: 0; }
        .legal-prose .legal-plain li { margin: 0; }
      `}</style>
    </div>
  );
}
